import React, { useEffect, useRef } from 'react'
import styles from './styles.module.css'

// Product turn played while the pointer is on the card (lib/shopHoverVideos).
// Nothing loads until the first hover (preload none, no src), so the shop pays
// nothing for it on load. The card keeps its photo until the video is actually
// playing: the card gets data-hover-video="on" on `playing` and loses it on
// leave, and the CSS swaps photo and video on that flag, so a slow first load
// never shows an empty card.
//
// Skipped (photo stays, no hover change): touch-only devices (no hover),
// reduced motion, and Safari on any card whose image background is not the MP4's
// own grey (`light`: false), because Safari can only play the opaque MP4.
function HoverVideo({ video, light }) {
  const ref = useRef(null)

  useEffect(() => {
    const el = ref.current
    const card = el && el.closest('a')
    if (!el || !card || typeof window === 'undefined') return undefined
    if (window.matchMedia('(hover: none)').matches) return undefined
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined
    const safari = /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(navigator.userAgent)
    const src = safari ? (light ? video.mp4 : null) : video.webm
    if (!src) return undefined

    let over = false
    const onPlaying = () => { if (over) card.dataset.hoverVideo = 'on' }
    const enter = () => {
      over = true
      if (!el.src) el.src = src
      el.currentTime = 0
      const p = el.play()
      if (p && p.catch) p.catch(() => {})
    }
    const leave = () => {
      over = false
      delete card.dataset.hoverVideo
      el.pause()
    }
    el.addEventListener('playing', onPlaying)
    card.addEventListener('pointerenter', enter)
    card.addEventListener('pointerleave', leave)
    return () => {
      el.removeEventListener('playing', onPlaying)
      card.removeEventListener('pointerenter', enter)
      card.removeEventListener('pointerleave', leave)
      delete card.dataset.hoverVideo
    }
  }, [video, light])

  return (
    <video
      ref={ref}
      className={styles.hoverVideo}
      width={video.width}
      height={video.height}
      muted
      loop
      playsInline
      preload="none"
      // Decorative: no controls, no download / playback-rate / cast menu items,
      // no picture-in-picture, no right-click menu.
      controls={false}
      controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
      disablePictureInPicture
      disableRemotePlayback
      onContextMenu={(e) => e.preventDefault()}
      aria-hidden="true"
      tabIndex={-1}
    />
  )
}

export default HoverVideo
